/**
 * 菜单模型（M2-02）。
 *
 * 纯函数：动作注册表 + 应用状态 → 可渲染的菜单树。MenuBar 只消费这个模型，
 * 不认识注册表——菜单内容因此永远与动作注册表一致（验收标准 1），
 * 且模型本身可以在无 DOM 的单元测试里验证。
 */
import {
  MENU_GROUPS,
  type ActionDefinition,
  type AppState,
  type MenuGroup,
} from '../../app/actions/registry';
import { formatShortcut, type Platform } from '../../app/actions/keymap';

export interface MenuItemModel {
  readonly id: string;
  readonly title: string;
  /** 右侧快捷键文案；没有快捷键则为空串。二级菜单的父项恒为空串。 */
  readonly shortcutText: string;
  /**
   * 二级菜单父项的 enabled 是"子项里有没有至少一个可用的"（§7.1 惯例：
   * 父项全灰才代表这一整支都没用，不是随便挑一个子项的状态）。
   */
  readonly enabled: boolean;
  /** 分节号。相邻项分节不同时之间画分隔线。 */
  readonly section: number;
  /**
   * 存在即为二级菜单父项：本身不可执行，点击/划过展开这个子列表
   * （Photoshop 的「编辑→变换→缩放/旋转/…」那种嵌套）。
   */
  readonly submenu?: readonly MenuItemModel[];
}

export interface MenuGroupModel {
  readonly group: MenuGroup;
  readonly items: readonly MenuItemModel[];
}

/**
 * 生成菜单模型。空组不出现——菜单栏只显示有内容的组。
 * `actions` 应传 `registry.list()`，它已按 组→节→序 排好；同一个 submenu
 * 标签的动作会被折成一个二级菜单父项，插在第一次出现的位置。
 */
export function buildMenuModel(
  actions: readonly ActionDefinition[],
  state: AppState,
  platform?: Platform,
): readonly MenuGroupModel[] {
  const byGroup = new Map<MenuGroup, MenuItemModel[]>();
  // group -> submenu 标签 -> 该组 items 数组里父项所在下标
  const submenuIndex = new Map<MenuGroup, Map<string, number>>();

  for (const action of actions) {
    const item: MenuItemModel = {
      id: action.id,
      title: action.title,
      shortcutText: action.shortcut === undefined ? '' : formatShortcut(action.shortcut, platform),
      enabled: action.enabled?.(state) ?? true,
      section: action.section,
    };
    let list = byGroup.get(action.group);
    if (list === undefined) {
      list = [];
      byGroup.set(action.group, list);
    }

    if (action.submenu === undefined) {
      list.push(item);
      continue;
    }

    let headers = submenuIndex.get(action.group);
    if (headers === undefined) {
      headers = new Map();
      submenuIndex.set(action.group, headers);
    }
    let headerAt = headers.get(action.submenu);
    if (headerAt === undefined) {
      headerAt = list.length;
      headers.set(action.submenu, headerAt);
      list.push({
        id: `submenu:${action.group}:${action.submenu}`,
        title: action.submenu,
        shortcutText: '',
        enabled: false,
        section: action.section,
        submenu: [],
      });
    }
    const header = list[headerAt]!;
    const children = [...header.submenu!, item];
    list[headerAt] = { ...header, submenu: children, enabled: children.some((c) => c.enabled) };
  }

  return MENU_GROUPS.filter((group) => byGroup.has(group)).map((group) => ({
    group,
    items: byGroup.get(group)!,
  }));
}
